﻿import {createContext, JSX, ReactNode, useCallback, useContext, useEffect, useMemo, useState} from "react";

export type FilterManagerProps<T> = {
	source: T[];
	onFiltered: (items: T[]) => void;
	children: ReactNode;
}
type FilteredItemsContextType<T> = {
	registerFilter: (id: string, filterFn: (items: T[]) => T[]) => void;
	unregisterFilter: (id: string) => void;
}

const FilteredItemsContext = createContext<FilteredItemsContextType<any> | null>(null);

export function FilterManager<T>(props: FilterManagerProps<T>): JSX.Element {
	const {source, onFiltered, children} = props;
	const [filters, setFilters] = useState<Map<string, (items: T[]) => T[]>>(new Map());

	const registerFilter = useCallback((id: string, filterFn: (items: T[]) => T[]) => {
		setFilters(prev => new Map(prev).set(id, filterFn));
	}, []);

	const unregisterFilter = useCallback((id: string) => {
		setFilters(prev => {
			const newMap = new Map(prev);
			newMap.delete(id);
			return newMap;
		});
	}, []);

	const context = useMemo(() => ({registerFilter, unregisterFilter}), [registerFilter, unregisterFilter]);

	useEffect(() => {
		let result = source;
		for (const filterFn of filters.values()) {
			result = filterFn(result);
		}

		onFiltered(result);
	}, [source, filters, onFiltered]);

	return (
		<FilteredItemsContext.Provider value={context}>
			{children}
		</FilteredItemsContext.Provider>
	)
}

export function useFilter<T>(id: string, filterFn: (items: T[]) => T[]): void {
	const context = useContext(FilteredItemsContext);

	useEffect(() => {
		if (context) {
			context.registerFilter(id, filterFn);
			return () => context.unregisterFilter(id);
		}

		return undefined;
	}, [context, id, filterFn]);
}
